import React, { JSX, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CellProps } from 'react-table';
import { CopyIcon, InfoIcon } from '@chakra-ui/icons';
import { Button, Flex, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import { Table, useToast } from 'common/components';

import { TABLE_CELL_EMPTY_VALUE } from '../../../../common/components/Table/utils/table.const';
import { DEFAULT_PAGINATED_TABLE_LIMIT } from '../../../../common/constants/common';
import { useDebounce } from '../../../../common/hooks/useDebounce';
import { getLocalDateTimeByUtcTimestamp } from '../../../../common/utils';
import { convertFeeInMinUnitToMainUnitFixed } from '../../../../common/utils/fee.helper';
import { DEBOUNCE_SEARCH_TIME } from '../../../../config/configConsts';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { useHtmlTranslations } from '../../../../contexts/LocalizationContext/hooks';
import { useGetUserPayments } from '../../../../services/application-users';
import { AssignedItemResponse } from '../model/assigned-item.response';
import { PaymentHistoryStatus } from '../model/enums/payment-history-status.enum';
import { PaymentHistoryResponse } from '../model/payment-history.response';
import { getItemTableCell, mapAssignedItemToEcoFeeReason, mapPaymentMethodTypeToLabel } from '../utils/eco-fee.helper';
import { buildPaymentStatusOptions } from '../utils/status-options.const';
import { BorrowingDetailsModal } from './BorrowingDetailsModal';
import { PaymentStatus } from './PaymentStatus';
import { StatusFilterSelect } from './StatusFilterSelect';

const PaymentsTable = (): JSX.Element => {
  const { userId } = useParams<{ userId: string }>();
  const [page, setPage] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | PaymentHistoryStatus>('all');
  const t = useTranslations();
  const htmlT = useHtmlTranslations();
  const debouncedSearch = useDebounce(searchText, DEBOUNCE_SEARCH_TIME);
  const { data, isFetching: isFetchingPayments } = useGetUserPayments(userId ?? null, {
    limit: DEFAULT_PAGINATED_TABLE_LIMIT,
    page,
    searchText: debouncedSearch,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const borrowingDetailsModal = useDisclosure();
  const [selectedItems, setSelectedItems] = useState<AssignedItemResponse[] | null>(null);
  const { displayToast } = useToast();

  const handleOpenModal = useCallback(
    (rows: AssignedItemResponse[]) => () => {
      setSelectedItems(rows);
      borrowingDetailsModal.onOpen();
    },
    [borrowingDetailsModal],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedItems(null);
    borrowingDetailsModal.onClose();
  }, [borrowingDetailsModal]);

  const handleSearchTextChange = useCallback((text: string) => {
    setPage(1);
    setSearchText(text);
  }, []);

  const handleStatusFilterChange = useCallback((val: string) => {
    setPage(1);
    setStatusFilter(val as 'all' | PaymentHistoryStatus);
  }, []);

  const copyPspIdToClipboard = useCallback(
    (original: PaymentHistoryResponse) => () => {
      navigator.clipboard.writeText(original.pspId as string).then(() => {
        displayToast('success', t('users_eco_fee.payments.toast.psp_id_copied'));
      });
    },
    [displayToast, t],
  );

  const getItemCell = useCallback(
    (original: PaymentHistoryResponse) => {
      const itemsLength = original.items.length;
      switch (itemsLength) {
        case 0:
          return t('users_eco_fee.payments.no_items');
        case 1:
          return getItemTableCell(original.items[0].product, t);
        default:
          return t('users_eco_fee.payments.items_many', { '{{count}}': String(itemsLength) });
      }
    },
    [t],
  );

  const columns = useMemo(
    () => [
      {
        id: 'transactionId',
        Header: t('users_eco_fee.payments.table.payment_id'),
        accessor: 'transactionId' as const,
      },
      {
        id: 'amount',
        Header: t('users_eco_fee.common.amount'),
        textAlign: 'right',
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<PaymentHistoryResponse>) => (
          <>
            {convertFeeInMinUnitToMainUnitFixed(original.amount)} {original.currency}
          </>
        ),
      },
      {
        id: 'transactionTimestamp',
        Header: t('users_eco_fee.payments.table.charged_at'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<PaymentHistoryResponse>) => <>{getLocalDateTimeByUtcTimestamp(original.transactionTimestamp)}</>,
      },
      {
        id: 'items',
        Header: t('users_eco_fee.payments.table.items'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<PaymentHistoryResponse>) => <>{getItemCell(original)}</>,
      },
      {
        id: 'reason',
        Header: t('users_eco_fee.common.reason'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<PaymentHistoryResponse>) => {
          if (original.items.length === 1) {
            return (
              <Flex justifyContent={'center'} alignItems={'center'} align="center" gap={2} maxW="380px">
                <Text flex="1 1 auto">{mapAssignedItemToEcoFeeReason(original.items[0], t)}</Text>
                <IconButton
                  aria-label={t('users_eco_fee.common.details')}
                  size="md"
                  variant={'ghost'}
                  onClick={handleOpenModal(original.items)}
                  icon={<InfoIcon color={'blueGrey.800'} />}
                ></IconButton>
              </Flex>
            );
          } else if (original.items.length === 0) {
            return <>{TABLE_CELL_EMPTY_VALUE}</>;
          } else {
            return (
              <Button size="sm" minW={'50px'} variant={'outline'} mx={1} onClick={handleOpenModal(original.items)}>
                {t('users_eco_fee.common.details')}
              </Button>
            );
          }
        },
      },
      {
        id: 'status',
        Header: t('users_eco_fee.payments.table.status'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<PaymentHistoryResponse>) => <PaymentStatus payment={original} />,
      },
      {
        id: 'paymentMethod',
        Header: t('users_eco_fee.payments.table.payment_method'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<PaymentHistoryResponse>) => (
          <>
            <Text>
              {original.paymentMethod
                ? mapPaymentMethodTypeToLabel(original.paymentMethod.type, t)
                : TABLE_CELL_EMPTY_VALUE}
            </Text>
            {original.paymentMethod?.type === 'sepa' && original.completionTimestamp && (
              <Text>
                {htmlT('users_eco_fee.payments.table.completed_at', {
                  '{{date}}': getLocalDateTimeByUtcTimestamp(original.completionTimestamp),
                })}
              </Text>
            )}
          </>
        ),
      },
      {
        id: 'pspId',
        Header: t('users_eco_fee.payments.table.psp_id'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<PaymentHistoryResponse>) => (
          <Flex align="center" gap={2} maxW="380px">
            <Text whiteSpace="normal" wordBreak="break-all" overflowWrap="anywhere" flex="1 1 auto">
              {original.pspId ?? TABLE_CELL_EMPTY_VALUE}
            </Text>
            {!!original.pspId && (
              <IconButton
                aria-label={t('users_eco_fee.payments.toast.psp_id_copied')}
                icon={<CopyIcon />}
                size="md"
                variant="ghost"
                onClick={copyPspIdToClipboard(original)}
              />
            )}
          </Flex>
        ),
      },
    ],
    [t, getItemCell, handleOpenModal, htmlT, copyPspIdToClipboard],
  );

  return (
    <>
      <Table<PaymentHistoryResponse>
        data={data?.items || []}
        columns={columns}
        isLoading={isFetchingPayments}
        paginationEnabled
        page={page}
        onPageChanged={setPage}
        totalPages={data?.totalPages}
        searchEnabled
        searchText={searchText}
        onSearchTextChanged={handleSearchTextChange}
        searchboxPlaceholder={t('users_eco_fee.payments.search_placeholder')}
      >
        <StatusFilterSelect
          label={t('users_eco_fee.payments.table.status') + ':'}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          options={buildPaymentStatusOptions(t)}
          containerProps={{ mb: -2 }}
        />
      </Table>
      <BorrowingDetailsModal
        isOpen={borrowingDetailsModal.isOpen}
        onClose={handleCloseModal}
        items={selectedItems ?? []}
      />
    </>
  );
};

export default PaymentsTable;
